import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import {
  fetchPartnerOeloxWallet,
  getPartnerProfile,
  setPartnerDriverTripFeeDefault,
  updatePartnerLogisticsSettings,
  updatePartnerLogo,
  uploadPartnerLogo,
  type PartnerDispatchMode,
} from "../lib/partnerSettings";
import type { PartnerDriverMode } from "../lib/partnerDrivers";
import { listPartnerClients } from "../lib/partnerClients";
import { listPartnerDrivers } from "../lib/partnerDrivers";
import { usePartnerSessionStore } from "../lib/partnerAuth";

function usePartnerId() {
  return usePartnerSessionStore((s) => s.session?.partnerId ?? null);
}

export function usePartnerProfile() {
  const partnerId = usePartnerId();
  return useQuery({
    queryKey: ["partner-profile", partnerId],
    enabled: !!partnerId,
    queryFn: () => getPartnerProfile(partnerId!),
  });
}

export function usePartnerOeloxWallet() {
  const partnerId = usePartnerId();
  return useQuery({
    queryKey: ["partner-oelox-wallet", partnerId],
    enabled: !!partnerId,
    queryFn: () => fetchPartnerOeloxWallet(partnerId!),
    refetchInterval: 60_000,
  });
}

export function useUpdatePartnerLogo() {
  const partnerId = usePartnerId();
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (file: File) => {
      if (!partnerId) throw new Error("no_partner");
      const url = await uploadPartnerLogo(partnerId, file);
      await updatePartnerLogo(partnerId, url);
      return url;
    },
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: ["partner-profile", partnerId] });
    },
  });
}

export function useUpdatePartnerLogistics() {
  const partnerId = usePartnerId();
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (patch: {
      dispatchMode: PartnerDispatchMode;
      oeloxFallbackEnabled: boolean;
      driverModeDefault?: PartnerDriverMode;
    }) => {
      if (!partnerId) throw new Error("no_partner");
      return updatePartnerLogisticsSettings(partnerId, patch);
    },
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: ["partner-profile", partnerId] });
      void qc.invalidateQueries({ queryKey: ["partner-fallback", partnerId] });
    },
  });
}

/** 170: default per-trip driver pay (Settings → Dispatch). */
export function useSetDriverTripFeeDefault() {
  const partnerId = usePartnerId();
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (amount: number) => {
      if (!partnerId) throw new Error("no_partner");
      return setPartnerDriverTripFeeDefault(partnerId, amount);
    },
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: ["partner-profile", partnerId] });
      void qc.invalidateQueries({ queryKey: ["partner-analytics-money"] });
      void qc.invalidateQueries({ queryKey: ["partner-analytics"] });
    },
  });
}

export function usePartnerSystemCounts() {
  const partnerId = usePartnerId();
  const clients = useQuery({
    queryKey: ["partner-clients", partnerId],
    enabled: !!partnerId,
    queryFn: () => listPartnerClients(partnerId!),
  });
  const drivers = useQuery({
    queryKey: ["partner-drivers", partnerId],
    enabled: !!partnerId,
    queryFn: () => listPartnerDrivers(partnerId!),
  });

  const clientRows = clients.data ?? [];
  const driverRows = drivers.data ?? [];
  const activeClients = clientRows.filter(
    (c) => c.tenant_status.toLowerCase() === "active"
  ).length;

  return {
    isLoading: clients.isLoading || drivers.isLoading,
    totalClients: clientRows.length,
    activeClients,
    activeDrivers: driverRows.length,
    onlineDrivers: driverRows.filter((d) => d.is_online).length,
  };
}
